import React, { useEffect, useState } from "react"
import { bool, number, oneOfType, string } from "prop-types"
import {
  sessionsParams,
  getLabels,
  labelParams
} from "components/admin/CheckInsStations/utils/requests"
import Icon from "components/planning_center_icon"
import { Pane } from "components/ui"
import {
  Box,
  Button,
  Divider,
  Icon as TapestryIcon,
  Link,
  StackView,
  Text
} from "@planningcenter/tapestry-react"
import SettingPane from "components/admin/legacySettingPanes"
import Labels from "./Labels"
import Sessions from "./sessions/Sessions"
import useAppEvent from "./hooks/use_app_event"
import useCheckInsSignupIntegration from "./hooks/use_check_ins_signup_integration"
import TriggerCheckistrationsResyncButton from "components/admin/AttendanceDropdown/TriggerCheckistrationsResyncButton"
import useAlreadySaafeUser from "./hooks/use_already_saafe_user"
import { IntegrationSetup } from "./IntegrationSetup"

const CHECK_INS_PURPLE = "#876096"

const CheckInsStations = ({
  signupId,
  subscribedToCheckIns = false,
  hasUsedLegacyCheckistrations,
  checkInsEventWizardUrl,
  apiBaseUrl,
  checkInsPermissions,
  isActingAsStaff
}) => {
  const [loading, setLoading] = useState(false)
  const [labelsLoading, setLabelsLoading] = useState(true)
  const [errors, setErrors] = useState([])
  const [isEditing, setIsEditing] = useState(false)
  const [status, setStatus] = useState(null)
  const [checkInsLabels, setCheckInsLabels] = useState([])
  const [firstTimeEnabled, setFirstTimeEnabled] = useState(false)
  const {
    loading: signupIntegrationLoading,
    errors: signupIntegrationErrors,
    record: signupIntegration
  } = useCheckInsSignupIntegration(signupId)
  const { loading: appEventLoading, errors: appEventErrors, appEvent } = useAppEvent(
    signupIntegration?.integrationId
  )
  const [initializing, setInitializing] = useState(true)
  const [dataToSave, setDataToSave] = useState(appEvent)
  const isCheckInsEditor = checkInsPermissions == "editor"
  const isCheckInsViewer = checkInsPermissions == "viewer"
  const isAtLeastCheckInsViewer = ["editor", "viewer"].includes(checkInsPermissions)
  const hasAccessToCheckIns = subscribedToCheckIns && isAtLeastCheckInsViewer
  const alreadySaafeUser = useAlreadySaafeUser({
    apiBaseUrl,
    hasPermission: isAtLeastCheckInsViewer
  })

  useEffect(() => {
    if (!hasAccessToCheckIns) {
      return
    }
    getLabels()
      .then(response => setCheckInsLabels(response.data))
      .catch(response => setErrors(response.errors))
      .finally(() => setLabelsLoading(false))
  }, [])

  useEffect(() => {
    if (!isAtLeastCheckInsViewer) {
      return
    }
    const signupIntegrationNotFound = signupIntegrationErrors.some(error => error.status === "404")
    const signupIntegrationAndAppEventLoaded = !!signupIntegration && !!appEvent

    if (signupIntegrationNotFound || signupIntegrationAndAppEventLoaded) setInitializing(false)
    setLoading(initializing || labelsLoading || signupIntegrationLoading || appEventLoading)
  }, [initializing, labelsLoading, signupIntegrationLoading, appEventLoading])

  useEffect(() => {
    if (!isAtLeastCheckInsViewer) {
      return
    }
    const filteredSignupIntegrationErrors = signupIntegrationErrors.filter(
      error => error.status !== "404"
    )

    return setErrors(
      errors
        .concat(filteredSignupIntegrationErrors)
        .concat(appEventErrors)
        .filter(Boolean) //remove all falsey values
    )
  }, [JSON.stringify([...signupIntegrationErrors, ...appEventErrors])])

  useEffect(() => {
    if (!isAtLeastCheckInsViewer) {
      return
    }
    setDataToSave(appEvent)
  }, [`${JSON.stringify(appEvent)} - ${loading}`])

  const handleAppEventChange = newAppEvent => {
    setDataToSave(newAppEvent)
  }

  const handleCreateSignupIntegration = () => {
    if (!isCheckInsEditor) {
      return
    }
    if (status !== "saving") {
      setStatus("saving")

      signupIntegration
        .create()
        .then(() => {
          setFirstTimeEnabled(true)
          setIsEditing(true)
        })
        .finally(() => setStatus(null))
    }
  }

  const handleDeleteSignupIntegration = () => {
    if (!isCheckInsEditor) {
      return
    }
    if (signupIntegration && status !== "saving") {
      setStatus("saving")

      signupIntegration
        .destroy()
        .then(() => setIsEditing(false))
        .finally(() => setStatus(null))
    }
  }

  const handleSave = () => {
    if (!isCheckInsEditor) {
      return
    }
    setIsEditing(false)
    appEvent.update({
      ...sessionsParams(dataToSave.appEventSessions),
      ...labelParams(dataToSave.appEventLabels)
    })
  }

  const handleCancel = () => {
    setDataToSave(appEvent)
    firstTimeEnabled ? handleDeleteSignupIntegration() : setIsEditing(false)
  }

  const NoSubscription = () => {
    return (
      <>
        With Check-Ins, you can print fully custom labels for your attendees and use stations to
        check them in when they arrive.
        <Button
          to="https://www.planningcenter.com/check-ins"
          target="_blank"
          rel="noreferrer"
          theme="generic"
          size="sm"
          title="Learn about Check-Ins"
          marginTop={1}
        />
      </>
    )
  }

  const DisabledWithLegacyView = () => {
    return (
      <>
        <IntegrationSetup signupId={signupId} checkInsEventWizardUrl={checkInsEventWizardUrl} />
        {isActingAsStaff && (
          <Box marginTop={1}>
            <TriggerCheckistrationsResyncButton event={event} />
          </Box>
        )}
      </>
    )
  }

  const DisabledNonEditor = () => {
    return (
      <>
        Only a Check-Ins Editor can manage the Check-Ins integration for this signup.
        <Button
          to="https://pcocheck-ins.zendesk.com/hc/en-us/articles/25103685567515"
          target="_blank"
          rel="noreferrer"
          title="Learn more"
          theme="generic"
          size="sm"
          marginTop={1}
        />
      </>
    )
  }

  const BlankState = () => {
    if (!subscribedToCheckIns) return <NoSubscription />
    else if (!isCheckInsEditor) return <DisabledNonEditor />
    else return <DisabledWithLegacyView />
  }

  const SettingsHeader = () => (
    <>
      <StackView alignment="center" axis="horizontal" gap={1}>
        <Icon
          symbol="logomark#check-ins"
          style={{ color: CHECK_INS_PURPLE }}
          className="font-size-1"
        />
        <Pane.Title className="pane__title--matchTable">Create Check-Ins event</Pane.Title>
      </StackView>
    </>
  )

  const DisableButton = () => (
    <Button
      disabled={status === "saving"}
      onClick={handleDeleteSignupIntegration}
      size="xs"
      theme="destroy"
      title="Disable"
    />
  )

  const BackToLegacy = () => {
    return (
      <Box marginTop={1} padding={2} className="line w-100%">
        <div className="font-size-4 c-neutral-600">
          <Text>
            You are using the Station Integration.{" "}
            {isCheckInsEditor ? (
              <button className="link" onClick={handleDeleteSignupIntegration}>
                Switch to our updated Registrations Integration.
              </button>
            ) : (
              <p className={"mb-0"}>
                To switch to our updated Registrations Integration, ask a Check-Ins administrator.
              </p>
            )}
          </Text>
        </div>
      </Box>
    )
  }

  const filteredSessions =
    dataToSave?.appEventSessions?.filter(appEventSession => !appEventSession._destroy) || []
  const filteredLabels =
    dataToSave?.appEventLabels?.filter(
      appEventLabel => !appEventLabel._destroy && appEventLabel.label
    ) || []

  const showNonSAAFEView =
    !subscribedToCheckIns || !isAtLeastCheckInsViewer || !signupIntegration?.integrationId

  return (
    <div className="max-width-center-stage">
      <StackView spacing={4}>
        <SettingPane
          title={<SettingsHeader />}
          editButtonContent={isCheckInsViewer ? null : <DisableButton />}
          showEditButton={isCheckInsEditor}
          isEditing={isEditing}
          isLoading={loading}
          status={status}
          error={errors[0]}
          isChangePending={isEditing}
          onIsEditing={() => {
            setIsEditing(!isEditing)
          }}
          onSave={handleSave}
          onCancel={handleCancel}
          useBlankState={showNonSAAFEView}
          blankState={<BlankState />}
          footer={
            hasUsedLegacyCheckistrations && (
              <BackToLegacy
                appEventLabels={filteredLabels}
                checkInsLabels={checkInsLabels}
                isEditing={isEditing}
                onAppEventChange={handleAppEventChange}
                signupLabelOptions={signupIntegration?.signupLabelOptions}
              />
            )
          }
        >
          <Sessions
            isEditing={isEditing}
            onAppEventChange={handleAppEventChange}
            sessions={filteredSessions}
            showInfoAlert={firstTimeEnabled && dataToSave?.appEventSessions?.length >= 5}
          />
          <Labels
            appEventLabels={filteredLabels}
            checkInsLabels={checkInsLabels}
            isEditing={isEditing}
            onAppEventChange={handleAppEventChange}
            signupLabelOptions={signupIntegration?.signupLabelOptions}
          />
        </SettingPane>

        {showNonSAAFEView && alreadySaafeUser && (
          <Pane>
            <Pane.Header>
              <Pane.Title>Station Integration</Pane.Title>
            </Pane.Header>
            <Pane.Body spacing={2}>
              <Text>
                Take attendance using a Check-Ins Station and keep your attendance data and
                reporting here in Registrations. Attendance and label printing are included, but it
                does not include check-outs, trust people, or pre-check in the Church Center app.{" "}
              </Text>

              <StackView
                alignment="center"
                axis="horizontal"
                backgroundColor="warning-lighter"
                paddingHorizontal={2.5}
                paddingVertical={1.5}
                radius={3}
                spacing={2.5}
              >
                <TapestryIcon
                  name="general.exclamationTriangle"
                  color="warning"
                  shrink={0}
                  size={3}
                />
                <Text color="info-light" size={4}>
                  <strong>Notice:</strong> The station integration will soon be going away while we
                  continue to research the feature. If you want to take attendance with full
                  Check-ins functionality, please use the <i>Create Check-Ins event</i> option
                  above. If you&apos;d like to provide feedback on this feature,{" "}
                  <Link external to="https://planningcenter.typeform.com/to/wY9ibl14">
                    fill out our survey
                  </Link>
                  !
                </Text>
              </StackView>

              <Divider />

              {isCheckInsEditor && (
                <StackView axis="horizontal" alignment="center" spacing={1.5}>
                  <Button
                    title="Enable the stations integration"
                    size="sm"
                    theme="link"
                    onClick={handleCreateSignupIntegration}
                  />
                </StackView>
              )}
            </Pane.Body>
          </Pane>
        )}
      </StackView>
    </div>
  )
}

CheckInsStations.propTypes = {
  signupId: oneOfType([string, number]).isRequired,
  subscribedToCheckIns: bool,
  hasUsedLegacyCheckistrations: bool,
  apiBaseUrl: string,
  checkInsEventWizardUrl: string.isRequired,
  checkInsPermissions: string,
  isActingAsStaff: bool
}
export default CheckInsStations
